import api from '../../api/client';
import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  Select,
  Modal,
  Alert,
  Badge,
  Row,
  Col,
  Space,
  message,
  ConfigProvider,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import ProTable from "@ant-design/pro-table";
import axios from "axios";
import config from "../../config";
import enUS from "antd/lib/locale/en_US";

const FarmItems = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [form] = Form.useForm();

  const apiUrl =
    window.location.hostname === "localhost"
      ? config.development.apiUrl
      : config.production.apiUrl;

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const res = await api.get(`/farm-items`);
      setItems(res.data);
    } catch (error) {
      message.error("Error fetching farm items");
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await api.post(`/farm-items`, values);
      message.success("Item added successfully!");
      setModalVisible(false);
      form.resetFields();
      fetchItems();
    } catch (error) {
      message.error(error.response?.data?.error || "Error adding item");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, isProtected) => {
    if (isProtected) {
      message.warning("Protected items cannot be deleted");
      return;
    }
    setDeleteConfirm(id);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/farm-items/${deleteConfirm}`);
      message.success("Item deleted successfully!");
      fetchItems();
    } catch (error) {
      message.error(error.response?.data?.error || "Error deleting item");
    } finally {
      setDeleteConfirm(null);
    }
  };

  const protectedItems = items.filter((item) => item.is_protected);
  const customItems = items.filter((item) => !item.is_protected);

  const itemTypeOptions = [
    { value: "poultry", label: "Poultry" },
    { value: "eggs", label: "Eggs" },
    { value: "weaners", label: "Weaners" },
    { value: "goats", label: "Goats" },
    { value: "sheep", label: "Sheep" },
    { value: "pigs", label: "Pigs" },
    { value: "other", label: "Other" },
  ];

  // ProTable columns for custom items
  const columns = [
    { title: "Category", dataIndex: "category", key: "category", sorter: true },
    { title: "Name", dataIndex: "name", key: "name", sorter: true },
    { title: "Unit", dataIndex: "unit", key: "unit" },
    {
      title: "Type",
      dataIndex: "item_type",
      key: "item_type",
      sorter: true,
      render: (type) =>
        itemTypeOptions.find((t) => t.value === type)?.label || type,
    },
    {
      title: "Status",
      key: "status",
      width: 100,
      render: (_, record) => (
        <Badge color={record.is_protected ? "blue" : "default"}>
          {record.is_protected ? "Protected" : "Custom"}
        </Badge>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(record.id, record.is_protected)}
          disabled={record.is_protected}
          title={
            record.is_protected
              ? "Protected items cannot be deleted"
              : "Delete item"
          }
        />
      ),
    },
  ];

  return (
    <Card
      title="Farm Items Management"
      bordered={false}
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setModalVisible(true)}
        >
          Add New Item
        </Button>
      }
    >
      {/* Alert area for any remaining errors (optional – we use message) */}

      {/* Protected Items Section – as Cards */}
      {protectedItems.length > 0 && (
        <>
          <h4 style={{ marginTop: 24, marginBottom: 12 }}>
            Protected Items (Cannot be deleted)
          </h4>
          <Row gutter={[16, 16]}>
            {protectedItems.map((item) => (
              <Col xs={24} sm={12} md={8} key={item.id}>
                <Card size="small" style={{ height: "100%" }}>
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <strong>{item.name}</strong>
                    <div>
                      <small className="text-muted">
                        {item.category} • {item.unit} •{" "}
                        {itemTypeOptions.find((t) => t.value === item.item_type)
                          ?.label || item.item_type}
                      </small>
                    </div>
                    <Badge color="blue">Protected</Badge>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}

      {/* Custom Items Section – ProTable */}
      <div style={{ marginTop: protectedItems.length > 0 ? 32 : 24 }}>
        <h4>Custom Items</h4>
        <>
          <ProTable
            dataSource={customItems}
            columns={columns}
            rowKey="id"
            search={false} // disable built‑in search, keep it simple
            pagination={{ defaultPageSize: 10, showSizeChanger: true }}
            toolBarRender={false}
            options={{ reload: true, density: true, setting: true }}
            dateFormatter="string"
          />
        </>
      </div>

      {/* Add / Edit Modal */}
      <Modal
        title="Add New Farm Item"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleSubmit}
          >
            Add Item
          </Button>,
        ]}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ item_type: "other" }}
        >
          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: "Please enter category" }]}
          >
            <Input placeholder="e.g. Poultry, Eggs" />
          </Form.Item>
          <Form.Item
            name="name"
            label="Item Name"
            rules={[{ required: true, message: "Please enter item name" }]}
          >
            <Input placeholder="e.g. Chicken, Kienyeji Eggs" />
          </Form.Item>
          <Form.Item
            name="unit"
            label="Unit"
            rules={[{ required: true, message: "Please enter unit" }]}
          >
            <Input placeholder="e.g. birds, pieces, animals" />
          </Form.Item>
          <Form.Item name="item_type" label="Item Type">
            <Select options={itemTypeOptions} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirm Delete"
        open={!!deleteConfirm}
        onCancel={() => setDeleteConfirm(null)}
        footer={[
          <Button key="cancel" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>,
          <Button key="delete" danger onClick={confirmDelete}>
            Delete
          </Button>,
        ]}
      >
        <p>
          Are you sure you want to delete this item? This action cannot be
          undone.
        </p>
      </Modal>
    </Card>
  );
};

export default FarmItems;
